// src/pages/Workshop3.js

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';

const Workshop3 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stripeLink = "https://buy.stripe.com/cNi14o5Jg9jm1Pc4dV9oc0M";

  return (
    <>
      <Helmet>
        <title>Workshop 3 | Little Hibba</title>
        <meta name="description" content="Roles You Can Do in Children’s Publishing" />
      </Helmet>

      <style>{`
        .workshop-hero { background-color: #f2f7fa; padding: 120px 0 60px 0; }
        .classy-heading { font-family: 'Playfair Display', serif; color: #1a365d; }
        .text-reading { font-size: 1.1rem; line-height: 1.8; color: #4a5568; }
        .sticky-card { position: sticky; top: 120px; border: none; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.08); overflow: hidden; }
        .list-icon { color: #d4af37; margin-right: 12px; font-size: 1.2rem; }
        ul.custom-list { list-style: none; padding-left: 0; }
        ul.custom-list li { margin-bottom: 15px; display: flex; align-items: flex-start; }
      `}</style>

      <section className="workshop-hero">
        <Container>
          <Row className="justify-content-center text-center">
            <Col md={10} lg={8}>
              <span className="badge bg-white text-primary border border-primary px-3 py-2 rounded-pill text-uppercase mb-4">Workshop 3</span>
              <h1 className="classy-heading display-4 fw-bold mb-4">Roles You Can Do in Children’s Publishing</h1>
              <p className="lead text-muted">Children’s publishing isn’t just about authors and illustrators. Discover where your skills fit – editorial, design, sales, digital, even as a Muslim parent or freelancer.</p>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5 bg-white">
        <Container>
          <Row className="g-5">
            <Col lg={7} className="text-reading">
              <h3 className="classy-heading h2 mb-4">Who it’s for</h3>
              <p className="mb-5">Muslim creatives, teachers, designers and community organisers curious about publishing careers beyond writing, who want flexible, meaningful work.</p>

              <h3 className="classy-heading h2 mb-4">What we’ll cover</h3>
              <ul className="custom-list mb-5">
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>Key departments: editorial, design/production, sales/marketing, digital content, rights – day‑to‑day realities.</div></li>
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>In‑house vs freelance vs publishing press paths, with family‑friendly options.</div></li>
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>How Muslim skills transfer: teaching to educational editing, community work to sales/events, and design with modesty awareness.</div></li>
                <li><i className="bi bi-check-circle-fill list-icon"></i> <div>Emerging digital roles: audiobooks, apps, social content for children’s brands.</div></li>
              </ul>

              <h3 className="classy-heading h2 mb-4">You’ll leave with</h3>
              <ul className="custom-list mb-5">
                <li><i className="bi bi-star-fill list-icon"></i> <div>Personal “role shortlist” matching your skills to 2–3 realistic publishing jobs.</div></li>
                <li><i className="bi bi-star-fill list-icon"></i> <div>Action plan for your top role (portfolio needs, job boards, networking scripts).</div></li>
                <li><i className="bi bi-star-fill list-icon"></i> <div>Professional language to pitch yourself as a Muslim publishing pro.</div></li>
              </ul>
              
              <hr className="my-5" />

              <h3 className="classy-heading h2 mb-4">About Your Instructor</h3>
              <div className="d-flex align-items-center mb-4">
                <img src="/images/sajeda/12.jpg" alt="Sajeda Faraz" className="rounded-circle me-4 shadow-sm" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                <div>
                  <h4 className="mb-1 classy-heading">Sajeda Faraz</h4>
                  <p className="text-muted mb-0">Creative Director & Mentor</p>
                </div>
              </div>
              <p>Led by Sajeda Faraz (that's me!), a creative director and mentor with over 15 years of experience in branding, design, and publishing, this live workshop offers more than theory.</p>
              <p>I’ve helped develop, position, and launch over 400+ children’s titles across indie and traditional publishing, and I know what makes a creator stand out in a crowded market. From writing bios that land school visits to crafting brands that attract publishers, I’ve done it all, and I’m here to walk you through it.</p>
              <p>This is an intimate, in-person experience designed to give you personalised feedback, direct access to expert insights, and the creative clarity you need to move forward with confidence.</p>
            </Col>

            <Col lg={5}>
              <Card className="sticky-card bg-light">
                <img src="/images/sajeda/9.jpg" alt="Workshop" className="img-fluid" style={{ borderBottom: '5px solid #1a365d' }} />
                <Card.Body className="p-4 p-md-5">
                  <h4 className="classy-heading mb-4">Workshop Details</h4>
                  <ul className="custom-list text-muted mb-4 fs-6">
                    <li><i className="bi bi-camera-video list-icon"></i> <div>4 x 60-minute live sessions (recorded for replay).</div></li>
                    <li><i className="bi bi-calendar-check list-icon"></i> <div>Weekly commitment (60 minutes every Thursday evening for 4 weeks).</div></li>
                    <li><i className="bi bi-chat-dots list-icon"></i> <div>Supportive Q&A + feedback on your work with ongoing support from Sajeda.</div></li>
                    <li><i className="bi bi-infinity list-icon"></i> <div>Lifetime access to all materials.</div></li>
                    <li><i className="bi bi-exclamation-triangle list-icon text-danger"></i> <div className="text-danger">I am unable to offer roll-overs of any missed sessions.</div></li>
                  </ul>
                  <div className="bg-white p-3 rounded-3 text-center mb-4 border">
                    <span className="text-muted d-block mb-1">Total Investment</span>
                    <span className="display-5 fw-bold" style={{ color: '#1a365d' }}>£189</span>
                  </div>
                  <a href={stripeLink} target="_blank" rel="noopener noreferrer" className="d-block">
                    <Button variant="primary" size="lg" className="w-100 rounded-pill fw-bold py-3 shadow-sm text-uppercase" style={{ letterSpacing: '1px' }}>Enrol Now via Stripe</Button>
                  </a>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Workshop3;